/** Resolve the existing Canvas selection without choosing an arbitrary Model. */
export interface TargetShape {
  readonly id: string;
  readonly name?: string;
  readonly children: readonly TargetShape[];
}
export interface TargetView {
  readonly model_UUID?: string;
  readonly hierarchy?: string;
  readonly view: readonly TargetShape[];
}
export type ModelTarget = { state: 'resolved'; modelUuid: string; hierarchy: string }
  | { state: 'empty' | 'ambiguous' };

export interface RepresentedModel { readonly modelUuid: string; readonly hierarchy: string; readonly name?: string; }

/** Return only canonical identities explicitly represented by Canvas View groups. */
export function representedModels(views: readonly TargetView[]): readonly RepresentedModel[] {
  const unique = new Map<string, RepresentedModel>();
  for (const view of views) {
    if (!view.model_UUID || !view.hierarchy || view.model_UUID === '__MULTIPLE__' || view.hierarchy === '__MULTIPLE__') continue;
    const name = view.view[0]?.name?.trim();
    unique.set(JSON.stringify([view.model_UUID, view.hierarchy]), {
      modelUuid: view.model_UUID, hierarchy: view.hierarchy, ...(name ? { name } : {})
    });
  }
  return [...unique.values()];
}

export function resolveModelTarget(views: readonly TargetView[], selected: readonly TargetShape[],
                                  fallback?: { model_UUID?: string; hierarchy?: string }): ModelTarget {
  const contains = (items: readonly TargetShape[], id: string): boolean =>
    items.some(item => item.id === id || contains(item.children, id));
  const identities = (candidates: readonly TargetView[]) => representedModels(candidates)
    .map(model => ({ state: 'resolved' as const, ...model }));
  if (selected.length && views.length) {
    const matches = selected.map(shape => views.filter(view => contains(view.view, shape.id)));
    if (matches.some(group => !group.length)) return { state: 'ambiguous' };
    const targets = identities(matches.flat());
    return targets.length === 1 ? targets[0] : { state: 'ambiguous' };
  }
  if (views.length) {
    const targets = identities(views);
    if (targets.length === 1) return targets[0];
    // The document can retain a former Model after other Models are loaded.
    return { state: 'ambiguous' };
  }
  if (fallback?.model_UUID && fallback.hierarchy) {
    return { state: 'resolved', modelUuid: fallback.model_UUID, hierarchy: fallback.hierarchy };
  }
  return { state: 'empty' };
}
